import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, IconButton, Box, CircularProgress, TextField } from '@mui/material';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';

// components
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const CustomPagination = ({
  page,
  handleNextPage,
  handlePreviousPage,
  hasNextPage,
  responseMessage,
  data,
  setPage
}) => {
  const messageRef = useRef(null);
  const [isEditingPage, setIsEditingPage] = useState(false);
  const [pageInput, setPageInput] = useState(String(page));
  function handleNothing() {}

  useEffect(() => {
    if (!messageRef.current) return;

    if (responseMessage?.message && responseMessage?.isTrue) {
      // Show message
      messageRef.current.style.opacity = '1';
      messageRef.current.style.visibility = 'visible';
    } else {
      // Hide message
      messageRef.current.style.opacity = '0';
      messageRef.current.style.visibility = 'hidden';
    }
  }, [responseMessage?.message, responseMessage?.isTrue]);

  const messageStyles = {
    success: {
      backgroundColor: '#c6ffcf',
      color: '#171D40',
      icon: <CheckCircleOutlineOutlinedIcon />
    },
    error: {
      backgroundColor: '#ffc6c6',
      color: '#171D40',
      icon: <HighlightOffOutlinedIcon />
    },
    warning: {
      backgroundColor: '#ffc107',
      color: '#171D40',
      icon: <WarningAmberRoundedIcon />
    }
  };

  const currentStyle = messageStyles[responseMessage?.severity] || {};

  const getCurrentPage = () => data?.metas?.currentPage ?? page;

  const handlePageSubmit = () => {
    setIsEditingPage(false);

    const parsed = parseInt(String(pageInput).trim(), 10);
    const currentPage = getCurrentPage();
    const totalPages = data?.metas?.totalPages;

    if (!String(pageInput).trim() || Number.isNaN(parsed) || parsed < 1) {
      setPageInput(String(currentPage));
      return;
    }

    let targetPage = parsed;
    if (totalPages && targetPage > totalPages) {
      targetPage = totalPages;
    }

    if (targetPage !== currentPage) {
      handlePreviousPage(null, targetPage);
    } else {
      setPageInput(String(currentPage));
    }
  };

  const handlePageInputChange = (event) => {
    const { value } = event.target;
    if (/^\d*$/.test(value)) {
      setPageInput(value);
    }
  };

  const handlePageInputKeyDown = (event) => {
    if (['-', '+', 'e', 'E', '.'].includes(event.key)) {
      event.preventDefault();
    }
    if (event.key === 'Enter') {
      handlePageSubmit();
    }
  };

  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
    >
      <Box
        ref={messageRef}
        sx={{
          backgroundColor: currentStyle.backgroundColor,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderRadius: '8px',
          padding: '8px 20px',
          width: 'fit-content',
          ml: 2,
          opacity: 0,
          visibility: 'hidden',
          transition: 'opacity 0.3s ease',
          height: '40px'
        }}
      >
        {currentStyle.icon}
        <Typography sx={{ color: 'black', fontWeight: 600, fontSize: '12px' }}>
          {responseMessage?.message}
        </Typography>
      </Box>

      {/* Right Side: Pagination Icons */}
      <Box display="flex" alignItems="center">
        <IconButton
          onClick={handlePreviousPage}
          disabled={page === 1 || data?.loading}
          aria-label="previous page"
          size="large"
          sx={{
            color: '#000000',
            minWidth: { lg: '24px', xl: '30px' },
            minHeight: { lg: '24px', xl: '30px' },
            svg: {
              minWidth: { lg: '24px', xl: '24px' },
              minHeight: { lg: '24px', xl: '30px' }
            }
          }}
        >
          <KeyboardDoubleArrowLeftRoundedIcon />
        </IconButton>
        <Typography
          color="textSecondary"
          sx={{ mr: 1, fontSize: '12px', cursor: page === 1 || data?.loading ? '' : 'pointer' }}
          onClick={page === 1 || data?.loading ? handleNothing : handlePreviousPage}
          data-testid="previous_page"
        >
          Previous
        </Typography>

        {isEditingPage ? (
          <TextField
            value={pageInput}
            onChange={handlePageInputChange}
            onBlur={handlePageSubmit}
            onKeyDown={handlePageInputKeyDown}
            autoFocus
            size="small"
            variant="outlined"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1 }}
            sx={{
              width: '40px',
              input: {
                py: 0.5,
                px: 1,
                textAlign: 'center',
                fontWeight: 700,
                fontSize: '14px',
                backgroundColor: 'transparent',
                color: '#1A1A3F',
                borderRadius: '6px'
              },
              '& fieldset': { border: '1px solid #C0C0C0' }
            }}
          />
        ) : (
          <Box
            mx={1}
            py={0.25}
            px={1}
            borderRadius="16px"
            border="1px solid #C0C0C0"
            fontWeight={700}
            minWidth="24px"
            height="28px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="14px"
            color="#1A1A3F"
            sx={{ cursor: 'pointer', bgcolor: 'transparent' }}
            onClick={() => {
              if (!data?.loading) {
                setIsEditingPage(true);
                setPageInput(String(getCurrentPage()));
              }
            }}
          >
            {data?.loading ? (
              <CircularProgress size={10} thickness={5} sx={{ color: '#1A1A3F' }} />
            ) : (
              page
            )}
          </Box>
        )}
        <Typography
          color="textSecondary"
          onClick={
            !hasNextPage ||
            data?.loading ||
            (data?.metas?.currentPage + 1 === data?.metas?.totalPages && data?.loading)
              ? handleNothing
              : handleNextPage
          }
          sx={{
            mx: 1,
            fontSize: '12px',
            cursor:
              !hasNextPage ||
              data?.loading ||
              (data?.metas?.currentPage + 1 === data?.metas?.totalPages && data?.loading)
                ? ''
                : 'pointer'
          }}
          data-testid="next_page"
        >
          Next
        </Typography>

        <IconButton
          onClick={handleNextPage}
          disabled={
            !hasNextPage ||
            data?.loading ||
            (data?.metas?.currentPage + 1 === data?.metas?.totalPages && data?.loading)
          }
          aria-label="next page"
          size="large"
          sx={{
            color: '#000000',
            minWidth: { lg: '24px', xl: '30px' },
            minHeight: { lg: '24px', xl: '30px' },
            svg: {
              minWidth: { lg: '24px', xl: '24px' },
              minHeight: { lg: '24px', xl: '30px' }
            }
          }}
        >
          <KeyboardDoubleArrowRightRoundedIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

CustomPagination.propTypes = {
  page: PropTypes.number,
  handleNextPage: PropTypes.func,
  handlePreviousPage: PropTypes.func,
  hasNextPage: PropTypes.bool,
  responseMessage: PropTypes.shape({
    message: PropTypes.string,
    severity: PropTypes.oneOf(['success', 'error', 'warning', ''])
  }),
  data: PropTypes.shape({
    metas: PropTypes.oneOfType([
      PropTypes.shape({
        currentPage: PropTypes.number,
        totalPages: PropTypes.number
      }),
      PropTypes.arrayOf(
        PropTypes.shape({
          currentPage: PropTypes.number,
          totalPages: PropTypes.number
        })
      )
    ]),
    loading: PropTypes.bool
  })
};

export default CustomPagination;
